import React from "react";
import { api } from "../api";

/** Coerce to number or null (handles strings, undefined, NaN) */
function num(v: any): number | null {
  const n = typeof v === "string" ? parseFloat(v) : (typeof v === "number" ? v : NaN);
  return Number.isFinite(n) ? n : null;
}

type NormRow = {
  symbol: string;
  price: number | null;
  change: number | null;
  changePct: number | null;
};

/** Try to normalize different backend shapes into a single row */
function normalize(r: any): NormRow | null {
  if (!r) return null;
  const symbol = String(r.symbol ?? r.ticker ?? "").toUpperCase();
  if (!symbol) return null;

  // price / last
  const last = num(r.last ?? r.price ?? r.close ?? r.ltp);

  // prior reference (if we need to compute)
  const prev = num(r.prev_close ?? r.prev ?? r.pc);

  // absolute change
  let ch = num(r.change ?? r.chg ?? r.delta);
  if (ch === null && last !== null && prev !== null) ch = last - prev;

  // percent change
  let pct = num(r.change_pct ?? r.chgPct ?? r.pct);
  if (pct === null && ch !== null && prev !== null && prev !== 0) {
    pct = (ch / prev) * 100;
  }

  return { symbol, price: last, change: ch, changePct: pct };
}

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 3 });
const pctFmt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

function fmtPrice(x: number | null) {
  return x === null ? "—" : usd.format(x);
}
function fmtChange(x: number | null) {
  if (x === null) return "—";
  const s = x >= 0 ? "+" : "";
  return s + x.toFixed(2);
}
function fmtPct(x: number | null) {
  if (x === null) return "—";
  const s = x >= 0 ? "+" : "";
  return s + pctFmt.format(x) + "%";
}

export default function Ticker() {
  const [rows, setRows] = React.useState<NormRow[]>([]);
  const [err, setErr] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      const raw = await api.ticker();           // any shape
      const mapped = (Array.isArray(raw) ? raw : []).map(normalize).filter(Boolean) as NormRow[];
      setRows(mapped);
      setErr(null);
    } catch (e: any) {
      setErr(String(e));
    }
  }, []);

  React.useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  // duplicate the list to create a seamless marquee
  const repeated = [...rows, ...rows];

  return (
    <div className="card">
      <div className="row">
        <h2 style={{ margin: 0 }}>Ticker</h2>
        <button onClick={load}>refresh</button>
      </div>
      {err && <div className="bad">{err}</div>}

      <div className="ticker-rail">
        <div className="ticker-track">
          {repeated.map((r, i) => {
            const up = (r.change ?? 0) >= 0;
            return (
              <div key={r.symbol + "-" + i} className="ti">
                <span className="u">{r.symbol}</span>{" "}
                {fmtPrice(r.price)}{" "}
                <span className={up ? "ok" : "bad"}>
                  {fmtChange(r.change)} ({fmtPct(r.changePct)})
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <small className="muted">Auto-scrolling. Updates every ~60s. Timestamps shown in New York time.</small>
    </div>
  );
}
